import { type Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { maxNumber, minNumber } from '@ps/web-shared-validators';
import React from 'react';
import { useField } from 'react-final-form';

import type { Limits } from '../../../../types/responsibleGamingLimits';
import {
  validateAmountLimit,
  validateResponsibleGamingLimit
} from '../../../../validators/responsibleGamingLimits';
import AmountCurrency from '../../../AmountCurrency/AmountCurrency';
import ConversionSubLabel from '../../../ManageCurrencies/components/ConversionSubLabel/ConversionSubLabel';
import useFormatDisabledLimit from '../../hooks/useFormatDisabledLimit';

import {
  TXTWCL_Must_be_less_than_or_equal_to__ELL,
  TXTWCL_N0_Hour_limit,
  TXTWCL_The_maximum_amount_for_this_li_ELL_1,
  TXTWCL_The_minimum_amount_for_this_li_ELL
} from './DailyLimit.messages.json';

interface Props {
  fieldName: string;
  limits: Limits;
  currency: Currency;
  limitsDisabled?: boolean;
  showConversionInfo?: boolean;
  withPlaceholder?: boolean;
}

const DailyLimit: React.FC<Props> = ({
  fieldName,
  currency,
  limits,
  limitsDisabled,
  withPlaceholder = false,
  ...props
}) => {
  const swcRmfShowOnlyWeeklyLimit = useFeature(FeatureFlag.SwcRmfShowOnlyWeeklyLimit);
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);
  const showConversionInfo = useFeature(FeatureFlag.SwcDepositLimitCurrencyConversionInfo);

  const { limitMax, limitMin, limit } = limits?.daily ?? {};

  useField(`${fieldName}.daily.previousLimit`, { initialValue: limit });
  const {
    input: { value: rate }
  } = useField(`${fieldName}.rate`, { subscription: { value: true } });
  const {
    input: { onChange: onDepositLimitsChange, value: dailyLimit }
  } = useField(`${fieldName}.daily.limit`, {
    initialValue: limit !== -1 ? limit : undefined,
    subscription: { value: true }
  });

  useFormatDisabledLimit(limitsDisabled, limit, onDepositLimitsChange);

  return (
    <AmountCurrency
      withPlaceholder={withPlaceholder}
      defaultCurrency={currency}
      disabled={limitsDisabled}
      includeCurrencies={[currency]}
      label={TXTWCL_N0_Hour_limit}
      values={{ 0: 24 }}
      currencyFieldName={`${fieldName}.currency`}
      amountFieldName={`${fieldName}.daily.limit`}
      onAmountBlur={() => {
        dailyLimit && onDepositLimitsChange(Number(dailyLimit).toFixed(2));
      }}
      extension={
        showConversionInfo && <ConversionSubLabel amount={dailyLimit} fromCurrency={currency} />
      }
      validators={[
        validateAmountLimit(
          minNumber,
          TXTWCL_The_minimum_amount_for_this_li_ELL,
          limitMin,
          fieldName,
          rate
        ),
        validateAmountLimit(
          maxNumber,
          TXTWCL_The_maximum_amount_for_this_li_ELL_1,
          limitMax,
          fieldName,
          rate
        ),
        validateResponsibleGamingLimit(
          `${fieldName}.weekly.limit`,
          limit,
          { ...TXTWCL_Must_be_less_than_or_equal_to__ELL, values: { 0: 7 } },
          maxNumber
        ),
        validateResponsibleGamingLimit(
          `${fieldName}.monthly.limit`,
          limit,
          { ...TXTWCL_Must_be_less_than_or_equal_to__ELL, values: { 0: 30 } },
          maxNumber
        )
      ]}
      autofocus={!swcRmfShowOnlyWeeklyLimit && isAutofocusEnabled}
      {...props}
    />
  );
};

export default DailyLimit;
